import React, { useEffect, useRef, useState, useCallback } from 'react';
import moment from 'moment';
import './TranscriptPanel.css';

const CloseIcon    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>;
const SearchIcon   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const DownloadIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const ClearIcon    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>;
const PlayIcon     = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>;

const AVATAR_COLORS = ['#00BCD4','#26C6DA','#00897B','#10B981','#4DD0E1','#00838F'];
const getAvatarColor = (name) => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const LANG_TAGS = {
  'en':    '🇺🇸 EN',
  'ja':    '🇯🇵 JA',
  'zh-CN': '🇨🇳 ZH',
  'vi':    '🇻🇳 VI',
  'bn':    '🇧🇩 BN',
};

const TranscriptItem = ({ entry, selfName, onPlayAudio, playingId, setPlayingId, showAvatar }) => {
  const isSelf = entry.user?.toLowerCase() === selfName?.toLowerCase();
  const initial = (entry.user || '?')[0].toUpperCase();
  const color   = getAvatarColor(entry.user || '?');
  const langTag = LANG_TAGS[entry.language] || '🌐';
  const isTranslation = entry.isTranslation;
  const isPlaying = playingId === entry.id;

  const handlePlay = () => {
    if (playingId) return;
    setPlayingId(entry.id);
    onPlayAudio(entry.text, entry.language, entry.audioUrl, () => {
      setPlayingId(null);
    });
  };

  return (
    <div className={`tx-item ${isSelf ? 'tx-item--self' : 'tx-item--other'} ${isTranslation ? 'tx-item--translation' : ''}`} style={{ animationDelay: '0ms' }}>
      {/* Avatar: only for other users, only on first message in a group */}
      {!isSelf && showAvatar && (
        <div className="tx-avatar" style={{ background: color, color: '#fff' }}>{initial}</div>
      )}
      {!isSelf && !showAvatar && (
        <div className="tx-avatar-spacer" />
      )}
      <div className="tx-bubble">
        <div className="tx-meta">
          {showAvatar && <span className="tx-name" style={{ color }}>{isSelf ? 'You' : entry.user}</span>}
          <span className="tx-lang-tag">{langTag}</span>
          {isTranslation && <span className="tx-translation-badge">Translation</span>}
          <span className="tx-time">{entry.time}</span>
        </div>
        <p className="tx-text">{entry.text}</p>
        {(entry.canPlay || entry.audioUrl) && (
          <button
            className={`tx-play-btn ${isPlaying ? 'tx-play-btn--playing' : ''} ${playingId && !isPlaying ? 'tx-play-btn--disabled' : ''}`}
            onClick={handlePlay}
            disabled={!!playingId}
          >
            <PlayIcon/> {isPlaying ? 'Playing...' : (entry.audioUrl ? 'Play Recording' : 'Play Translation')}
          </button>
        )}
      </div>
    </div>
  );
};

const TranscriptPanel = ({ isOpen, onClose, selfName, messages, onPlayAudio }) => {
  const [search,    setSearch]    = useState('');
  const [autoScroll,setAutoScroll]= useState(true);
  const [playingId, setPlayingId] = useState(null);
  const bottomRef = useRef(null);
  const listRef   = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (autoScroll && listRef.current) {
      // Use requestAnimationFrame to ensure DOM has rendered the new message
      requestAnimationFrame(() => {
        if (listRef.current) {
          listRef.current.scrollTop = listRef.current.scrollHeight;
        }
      });
    }
  }, [messages, autoScroll]);

  // Detect if user scrolled up (disable auto-scroll)
  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 60;
    setAutoScroll(isAtBottom);
  }, []);

  const filtered = messages.filter(m =>
    !search || m.text?.toLowerCase().includes(search.toLowerCase()) || m.user?.toLowerCase().includes(search.toLowerCase())
  );

  const downloadTranscript = () => {
    const lines = messages.map(m =>
      `[${m.time}] ${m.user || 'Unknown'} (${m.language || '?'}): ${m.text}`
    ).join('\n');
    const header = `=== Meeting Transcript ===\nGenerated: ${moment().format('YYYY-MM-DD HH:mm:ss')}\nParticipant: ${selfName}\n\n`;
    const blob = new Blob([header + lines], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `transcript-${moment().format('YYYY-MM-DD-HHmm')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearTranscript = () => {
    if (window.confirm('Clear the transcript? This cannot be undone.')) {
      // Emit event for parent to handle
      window.dispatchEvent(new CustomEvent('clearTranscript'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="tx-panel">

      {/* Header */}
      <div className="tx-header">
        <div className="tx-header-left">
          <div className="tx-header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
          </div>
          <div>
            <p className="tx-header-title">Live Transcript</p>
            <p className="tx-header-sub">{messages.length} messages</p>
          </div>
        </div>
        <div className="tx-header-actions">
          <button className="tx-icon-btn" onClick={downloadTranscript} title="Download transcript">
            <DownloadIcon/>
          </button>
          <button className="tx-icon-btn" onClick={clearTranscript} title="Clear transcript">
            <ClearIcon/>
          </button>
          <button className="tx-icon-btn tx-icon-btn--close" onClick={onClose} title="Close">
            <CloseIcon/>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="tx-search">
        <SearchIcon/>
        <input
          type="text"
          placeholder="Search transcript…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="tx-search-input"
        />
        {search && (
          <button className="tx-search-clear" onClick={() => setSearch('')}>✕</button>
        )}
      </div>

      {/* Messages */}
      <div className="tx-list" ref={listRef} onScroll={handleScroll}>
        {filtered.length === 0 ? (
          <div className="tx-empty">
            <div className="tx-empty-icon">🎤</div>
            <p className="tx-empty-title">No transcript yet</p>
            <p className="tx-empty-desc">Start the interpretation to see live transcripts here.</p>
          </div>
        ) : (
          filtered.map((msg, i) => {
            const prevUser = i > 0 ? filtered[i - 1].user : null;
            const showAvatar = msg.user?.toLowerCase() !== prevUser?.toLowerCase();
            return (
              <TranscriptItem
                key={msg.id || i}
                entry={msg}
                selfName={selfName}
                onPlayAudio={onPlayAudio}
                playingId={playingId}
                setPlayingId={setPlayingId}
                showAvatar={showAvatar}
              />
            );
          })
        )}
        <div ref={bottomRef}/>
      </div>

      {/* Auto-scroll indicator */}
      {!autoScroll && messages.length > 0 && (
        <button className="tx-scroll-btn" onClick={() => {
          setAutoScroll(true);
          if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }}>
          ↓ Jump to latest
        </button>
      )}
    </div>
  );
};

export default TranscriptPanel;
